/**
 * CRM Export admin page
 *
 * Select CRM sections + optional date range, then download as a ZIP of CSVs.
 */
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'

const SECTIONS: { key: string; label: string; description: string }[] = [
  { key: 'clients',        label: 'Clients',        description: 'Client records, status, and notes' },
  { key: 'leads',          label: 'Leads',          description: 'Tracked leads with call signs and status' },
  { key: 'tasks',          label: 'Tasks',          description: 'Tasks and follow-up items' },
  { key: 'communications', label: 'Communications', description: 'Call, email, and meeting log entries' },
  { key: 'notes',          label: 'Notes',          description: 'Client note history' },
]

const DEFAULT_SELECTIONS: Record<string, boolean> = Object.fromEntries(
  SECTIONS.map((s) => [s.key, true]),
)

export default function CRMExportPage() {
  const { user } = useAuth()

  const [selections, setSelections] = useState<Record<string, boolean>>(DEFAULT_SELECTIONS)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const [exportSuccess, setExportSuccess] = useState(false)
  const [rateLimited, setRateLimited] = useState(false)

  const anySelected = Object.values(selections).some(Boolean)

  async function handleExport() {
    if (!anySelected || exporting) return

    setExporting(true)
    setExportError('')
    setExportSuccess(false)
    setRateLimited(false)

    const selectedSections = Object.entries(selections)
      .filter(([, checked]) => checked)
      .map(([key]) => key)

    const body: Record<string, unknown> = { sections: selectedSections }
    if (startDate) body.start_date = new Date(startDate).toISOString()
    if (endDate) {
      // Include the full end day by setting time to end of day
      const end = new Date(endDate)
      end.setHours(23, 59, 59, 999)
      body.end_date = end.toISOString()
    }

    try {
      const response = await fetch('/api/v1/admin/export/crm', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (response.status === 429) {
        setRateLimited(true)
        return
      }

      if (!response.ok) {
        let detail = `Export failed (HTTP ${response.status})`
        try {
          const data = await response.json()
          if (data?.detail) detail = String(data.detail)
        } catch {
          // ignore parse errors
        }
        setExportError(detail)
        return
      }

      // Trigger download from blob
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      a.download = `crm_export_${today}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 4000)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  if (!user?.is_superuser) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">CRM Export</h1>
      <p className="text-sm text-gray-500 mb-8">
        Export CRM data as a ZIP archive containing one CSV file per selected section.
        Rate-limited to 2 exports per hour.
      </p>

      {/* Section selection */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-3">Sections to export</h2>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {SECTIONS.map(({ key, label, description }) => (
            <label key={key} className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={selections[key] ?? false}
                onChange={(e) =>
                  setSelections((prev) => ({ ...prev, [key]: e.target.checked }))
                }
              />
              <div>
                <span className="text-sm font-medium text-gray-800">{label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              </div>
            </label>
          ))}
        </div>
        {!anySelected && (
          <p className="mt-2 text-xs text-red-600">Select at least one section to export.</p>
        )}
      </section>

      {/* Optional date range */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-3">Filter by date range (optional)</h2>
        <p className="text-xs text-gray-500 mb-3">
          When set, only records created within this range are included.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
        {startDate && endDate && endDate < startDate && (
          <p className="mt-2 text-xs text-red-600">End date must be on or after start date.</p>
        )}
      </section>

      {/* Messages */}
      {rateLimited && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          You can only export twice per hour. Please wait before trying again.
        </div>
      )}
      {exportError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {exportError}
        </div>
      )}
      {exportSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          Export downloaded successfully.
        </div>
      )}

      <button
        onClick={handleExport}
        disabled={exporting || !anySelected || (!!startDate && !!endDate && endDate < startDate)}
        className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {exporting ? 'Preparing download…' : 'Download CRM Export'}
      </button>
    </div>
  )
}
